import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Card, Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import Income from '@mui/icons-material/PlaylistAddCheck';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const IncomeSourceGeneralDataCard = ({ incomeItem, dispatch, incomeSourceAreas, partnerTypes, onSave, validation }) => {
  const { t } = useTranslation();
  const onPropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };
  const onPropertyChanged2 = propertyName => value => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };
  const onGeneralDataSave = () => {
    onSave('HeaderAddOrUpdate', incomeItem);
  };

  return (
    <Card
      icon={Income}
      iconColor='primary'
      title={t('IncomeSources.GeneralData')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('IncomeSources.SaveChanges')}
          onClick={onGeneralDataSave}
          disabled={!incomeItem.changed || !(validation |> isValid)}
        />
      }
    >
      <Prompt when={incomeItem.changed} message={t('IncomeSources.LeavingWithoutSavingIncomeSource')} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            isNumeric
            fullWidth
            value={incomeItem.orderNo}
            decimalScale={0}
            label={t('IncomeSources.OrderNo')}
            onChange={onPropertyChanged2('orderNo')}
            error={!isValid(validation.orderNo)}
            helperText={getErrors(validation.orderNo)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={incomeItem.code || ''}
            onChange={event => onPropertyChanged('code', event)}
            label={t('IncomeSources.IncomeCode')}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={incomeItem.name || ''}
            onChange={event => onPropertyChanged('name', event)}
            label={t('IncomeSources.IncomeName')}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            value={incomeItem.description || ''}
            onChange={event => onPropertyChanged('description', event)}
            label={t('IncomeSources.IncomeDescription')}
            error={!isValid(validation.description)}
            helperText={getErrors(validation.description)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={incomeItem.incomeSourceArea}
            simpleValue
            options={incomeSourceAreas}
            onChange={event => onPropertyChanged('incomeSourceArea', event)}
            label={t('IncomeSources.IncomeSourceArea')}
            error={!isValid(validation.incomeSourceArea)}
            helperText={getErrors(validation.incomeSourceArea)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={incomeItem.partnerTypeId}
            simpleValue
            options={partnerTypes}
            onChange={event => onPropertyChanged('partnerTypeId', event)}
            label={t('IncomeSources.PartnerType')}
            isSearchable
            isClearable
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(incomeItem.isActive) || false}
                onChange={event => onPropertyChanged('isActive', event.target.checked)}
                color='primary'
              />
            }
            label={t('IncomeSources.IsActive')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(incomeItem.fromExternalSource) || false}
                onChange={event => onPropertyChanged('fromExternalSource', event.target.checked)}
                color='primary'
              />
            }
            label={t('IncomeSources.FromExternalSource')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(incomeItem.isExpense) || false}
                onChange={event => onPropertyChanged('isExpense', event.target.checked)}
                color='primary'
              />
            }
            label={t('IncomeSources.IsExpense')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(incomeItem.hasSummary) || false}
                onChange={event => onPropertyChanged('hasSummary', event.target.checked)}
                color='primary'
              />
            }
            label={t('IncomeSources.HasSummary')}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

IncomeSourceGeneralDataCard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  incomeItem: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  incomeSourceAreas: PropTypes.array.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired
};

export default IncomeSourceGeneralDataCard;
